import { css, useTheme } from "@emotion/react";
import firebase from "firebase";
import { motion } from "framer-motion";
import { mix, rem } from "polished";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useClickAway } from "react-use";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { Flex, Label, Spacer, Text } from "../../base";
import { appDataAtomByPoolType } from "../../modules/apps";
import { useFirebase } from "../../modules/firebase";
import { AmpCutout, CircleClose, MagnifyingGlass } from "../Icons";
import { Input, SearchContainer, SearchSection, SearchTag } from "./Search";
import SearchItem from "./SearchItem";
import {
  explorerOpenAtom,
  searchResultsAtom,
  searchResultsSelector,
  searchTermAtom,
} from "./atoms";

const useSearchResults = () => {
  const searchTerm =
    useRecoilValue(searchTermAtom)?.replace(/^(0x)?/i, "") ?? "";
  const setSearchResults = useSetRecoilState(searchResultsAtom);
  const app = useFirebase();

  const start = searchTerm;
  const end = `${searchTerm}f`;

  useEffect(() => {
    if (!start) {
      setSearchResults([]);
      return;
    }

    void (async () => {
      const results = await app
        ?.firestore()
        .collection("accounts")
        .orderBy(firebase.firestore.FieldPath.documentId())
        .startAt(start)
        .endAt(end)
        .limit(10)
        .get();

      setSearchResults(
        results.docs.map((doc) => ({
          ...doc.data(),
          address: doc.id,
        }))
      );
    })();
  }, [start, end, app, setSearchResults]);
};

const Explorer = () => {
  useSearchResults();

  const { t } = useTranslation();

  const apps = useRecoilValue(appDataAtomByPoolType("app"));
  const transformers = useRecoilValue(appDataAtomByPoolType("transformer"));
  const { type: slugType, slug } = useParams<{ type: string; slug: string }>();

  const [explorerOpen, setExplorerOpen] = useRecoilState(explorerOpenAtom);

  const [searchTerm, setSearchTerm] = useRecoilState(searchTermAtom);
  const searchResults = useRecoilValue(searchResultsSelector);

  const ref = useRef(null);
  useClickAway(ref, () => {
    setExplorerOpen(false);
  });

  const { color: colors } = useTheme();
  const color = mix(0.5, colors.start, colors.end);

  const active = explorerOpen || Boolean(slug);

  return (
    <motion.div
      css={css`
        width: ${active ? "100%" : rem(400)};
        min-width: ${active ? rem(620) : "auto"};
        max-width: 100%;

        align-self: flex-start;
        margin-top: -${rem(6)};
      `}
      // animate={{
      //   width: active || slug ? '100%' : rem(400),
      // }}
    >
      <SearchContainer
        ref={ref}
        active={active}
        onClick={!explorerOpen && (() => setExplorerOpen(true))}
      >
        <Flex
          center={!active}
          css={css`
            padding: 0 ${rem(20)};
            height: ${rem(60)};
          `}
        >
          <MagnifyingGlass
            color={color}
            css={css`
              width: ${rem(24)};
              height: ${rem(24)};
            `}
          />
          <Spacer col size={10} />
          {slug ? (
            <SearchTag slug={slug} type={slugType} />
          ) : explorerOpen ? (
            <Input
              autoFocus
              placeholder={t("explore.inputPlaceholder")}
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
            />
          ) : (
            <Text color={color} fontSize={24}>
              Explore
            </Text>
          )}
          {slug ? (
            <Link
              css={css`
                position: absolute;
                right: ${rem(12)};
              `}
              to="/"
            >
              <CircleClose
                css={css`
                  width: ${rem(24)};
                  height: ${rem(24)};
                `}
                fill={color}
              />
            </Link>
          ) : null}
        </Flex>
        {explorerOpen ? (
          <div
            css={css`
              max-height: 66vh;
              overflow: scroll;
              padding: 0 ${rem(20)};
            `}
          >
            <Spacer size={8} />
            <SearchSection>
              <Label color="light">{t("explore.appsOnFlexa")}</Label>
              <Spacer size={10} />
              {apps.map((app) => (
                <SearchItem
                  key={app.appId}
                  icon={app.logo}
                  itemType="app"
                  slug={app.appId}
                  text={app.name}
                />
              ))}
            </SearchSection>
            <Spacer size={20} />
            <SearchSection>
              <Label color="light">{t("explore.transformersOnFlexa")}</Label>
              <Spacer size={10} />
              {transformers.map((app) => (
                <SearchItem
                  key={app.appId}
                  icon={app.logo}
                  itemType="transformer"
                  slug={app.appId}
                  text={app.name}
                />
              ))}
            </SearchSection>
            <Spacer size={20} />
            <SearchSection>
              <Label color="light">
                {searchTerm
                  ? t("explore.searchResults")
                  : t("explore.topSuppliers")}
              </Label>
              <Spacer size={10} />
              {searchResults?.map((v) => (
                <SearchItem
                  key={v.address}
                  icon={AmpCutout}
                  itemType="address"
                  right={v.totalSupply}
                  slug={`0x${v.address}`}
                  text={`0x${v.address}`}
                />
              )) ?? null}
            </SearchSection>
            <Spacer size={28} />
          </div>
        ) : null}
      </SearchContainer>
    </motion.div>
  );
};

export default Explorer;
